/**
 * Buttons.
 *
 * Two components and one table of styles, because the alternative — every screen writing its own
 * `className` string — is how an application ends up with nine slightly different buttons and no
 * way to change any of them. The table below is the whole vocabulary of pressable things in
 * Orbit Mind.
 *
 * Two rules are enforced by the types rather than by review:
 *
 * - **An icon-only button takes a `label` it cannot omit.** An icon has no accessible name, so a
 *   control wearing one is announced as nothing at all unless something supplies the words.
 * - **Every control is at least a finger tall.** `md` is 40px and `lg` is 44px, which is where
 *   both platform guidelines land, and this application is going to the app stores.
 *
 * `ref` is an ordinary prop rather than a `forwardRef` wrapper: React 19 passes it through to
 * function components directly, and Radix needs it to attach a trigger to its menu.
 */

import { touchFeedback } from './touchFeedback';
export type ButtonTone = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/*
 * The active scale is not decoration. On a touch screen there is no hover, so a control that
 * only responds to a cursor gives a finger no feedback at all — and an editor whose buttons feel
 * dead is one people press twice.
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium select-none ' +
  'transition-[background-color,border-color,color,box-shadow,transform,filter] duration-150 ease-out ' +
  'active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40';

const TONE: Readonly<Record<ButtonTone, string>> = {
  primary: 'bg-prisma text-accent-ink shadow-sm hover:brightness-110 hover:shadow-md',
  secondary:
    'border border-edge bg-surface text-ink hover:border-edge-strong hover:bg-surface-sunken',
  ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  danger: 'border border-danger/40 text-danger hover:bg-danger/10',
};

const SIZE: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-[0.9375rem]',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly tone?: ButtonTone;
  readonly size?: ButtonSize;
  readonly ref?: React.Ref<HTMLButtonElement>;
}

export function Button({
  tone = 'secondary',
  size = 'md',
  className = '',
  onClick,
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      className={`${BASE} ${TONE[tone]} ${SIZE[size]} ${className}`}
      {...rest}
      onClick={(event) => {
        touchFeedback();
        onClick?.(event);
      }}
    />
  );
}

const ICON_SIZE: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-11 w-11',
};

export interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  /**
   * The accessible name, and the tooltip.
   *
   * Not optional, and not defaultable: a control named only by its glyph is read out as "graphic"
   * and means nothing to anyone who cannot see it.
   */
  readonly label: string;
  /** Appended to the tooltip after a separator — a keyboard shortcut, usually. */
  readonly hint?: string;
  readonly icon: React.ReactNode;
}

export function IconButton({
  label,
  hint,
  icon,
  tone = 'ghost',
  size = 'md',
  className = '',
  onClick,
  ...rest
}: IconButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={hint === undefined ? label : `${label} · ${hint}`}
      className={`${BASE} ${TONE[tone]} ${ICON_SIZE[size]} shrink-0 p-0 ${className}`}
      {...rest}
      onClick={(event) => {
        touchFeedback();
        onClick?.(event);
      }}
    >
      {icon}
    </button>
  );
}

/**
 * A section heading inside a panel.
 *
 * Small, spaced and quiet. Repeated in five panels, which is four more than is worth writing the
 * same three classes for.
 */
export function PanelHeading({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <h3
      className={`text-ink-faint text-[0.6875rem] font-semibold tracking-[0.08em] uppercase ${className}`}
    >
      {children}
    </h3>
  );
}

/** A labelled switch. The whole row is the label; the track says on or off at a glance. */
export function SwitchRow({
  id,
  checked,
  onChange,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span id={`${id}-label`} className="text-ink text-sm">
        {children}
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        onClick={() => {
          onChange(!checked);
        }}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-prisma' : 'bg-surface-sunken border-edge border'
        }`}
      >
        <span
          className={`absolute top-1/2 left-1 h-5 w-5 -translate-y-1/2 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}
